import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Buildings,
  EnvelopeSimple,
  MapPin,
  Upload,
  Users,
} from '@phosphor-icons/react';
import { listMyOrganizations, getOrgGeneral, updateOrganizationGeneral } from '@/api/orgs';
import { orgNumberTerm } from '@/data/orgNumber';
import { AddressAutocomplete } from '@/onboarding/AddressAutocomplete';
import { LogoUploadField } from '@/components/ui/logo-upload-field';
import { FormSkeleton } from '@/components/ui/form-skeleton';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ImportMembersModal } from '@/components/ImportMembersModal';
import { LocationImportDialog } from '@/components/locations/LocationImportDialog';
import { useToast } from '@/components/Toast';

export function General() {
  const { slug = '' } = useParams();
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();

  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const org = orgsQuery.data?.find((o) => o.slug === slug);

  const generalQuery = useQuery({
    queryKey: ['org-general', slug],
    queryFn: () => getOrgGeneral(token, slug),
    enabled: !!token && !!slug,
  });

  const [name, setName] = useState('');
  const [initialLogoKey, setInitialLogoKey] = useState('');
  const [logoKey, setLogoKey] = useState('');
  // Address + contact state. Each pair tracks the current input value and
  // the value the server returned on the last successful save, so we can
  // diff them at mutation time and only PATCH the fields the user touched.
  const [street, setStreet] = useState('');
  const [initialStreet, setInitialStreet] = useState('');
  const [postal, setPostal] = useState('');
  const [initialPostal, setInitialPostal] = useState('');
  const [officeCity, setOfficeCity] = useState('');
  const [initialOfficeCity, setInitialOfficeCity] = useState('');
  // Office coordinates from the address picker. null = no pin. Tracked against
  // their saved values so a fresh pick is sent (and moves the map pin).
  const [lat, setLat] = useState<number | null>(null);
  const [initialLat, setInitialLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [initialLng, setInitialLng] = useState<number | null>(null);
  // Degraded fallback: when the geocoder is unavailable (503, e.g. no API key
  // in dev) we drop back to the hand-typed inputs, which carry no coordinates.
  const [addressUnavailable, setAddressUnavailable] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [initialContactEmail, setInitialContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [initialContactPhone, setInitialContactPhone] = useState('');
  const [regNumber, setRegNumber] = useState('');
  const [initialRegNumber, setInitialRegNumber] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [locationImportOpen, setLocationImportOpen] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!org) return;
    setName(org.name);
  }, [org]);

  useEffect(() => {
    if (!generalQuery.data) return;
    const g = generalQuery.data;
    setInitialLogoKey(g.logo_object_key);
    setLogoKey(g.logo_object_key);
    setInitialStreet(g.office_street);
    setStreet(g.office_street);
    setInitialPostal(g.office_postal_code);
    setPostal(g.office_postal_code);
    setInitialOfficeCity(g.office_city);
    setOfficeCity(g.office_city);
    setInitialLat(g.office_latitude);
    setLat(g.office_latitude);
    setInitialLng(g.office_longitude);
    setLng(g.office_longitude);
    setInitialContactEmail(g.contact_email);
    setContactEmail(g.contact_email);
    setInitialContactPhone(g.contact_phone);
    setContactPhone(g.contact_phone);
    setInitialRegNumber(g.org_registration_number);
    setRegNumber(g.org_registration_number);
  }, [generalQuery.data]);

  const save = useMutation({
    mutationFn: () => {
      const body: {
        name?: string;
        logo_object_key?: string;
        office_street?: string;
        office_postal_code?: string;
        office_city?: string;
        office_latitude?: number;
        office_longitude?: number;
        contact_email?: string;
        contact_phone?: string;
        org_registration_number?: string;
      } = {};
      if (org && name.trim() !== org.name) body.name = name.trim();
      if (logoKey !== initialLogoKey) body.logo_object_key = logoKey;
      if (street.trim() !== initialStreet) body.office_street = street.trim();
      if (postal.trim() !== initialPostal) body.office_postal_code = postal.trim();
      if (officeCity.trim() !== initialOfficeCity) body.office_city = officeCity.trim();
      // Coordinates ride along when a fresh place was picked. Clearing the
      // address (office_street: "") nulls them server-side, so we only ever
      // send a set pair here.
      if (lat != null && lng != null && (lat !== initialLat || lng !== initialLng)) {
        body.office_latitude = lat;
        body.office_longitude = lng;
      }
      if (contactEmail.trim() !== initialContactEmail) body.contact_email = contactEmail.trim();
      if (contactPhone.trim() !== initialContactPhone) body.contact_phone = contactPhone.trim();
      if (regNumber.trim() !== initialRegNumber) body.org_registration_number = regNumber.trim();
      return updateOrganizationGeneral(token, slug, body);
    },
    onSuccess: () => {
      toast.success(t('settings.organization.saved'));
      setErrorCode(null);
      setInitialLogoKey(logoKey);
      setInitialStreet(street.trim());
      setStreet(street.trim());
      setInitialPostal(postal.trim());
      setPostal(postal.trim());
      setInitialOfficeCity(officeCity.trim());
      setOfficeCity(officeCity.trim());
      setInitialLat(lat);
      setInitialLng(lng);
      setInitialContactEmail(contactEmail.trim());
      setContactEmail(contactEmail.trim());
      setInitialContactPhone(contactPhone.trim());
      setContactPhone(contactPhone.trim());
      setInitialRegNumber(regNumber.trim());
      setRegNumber(regNumber.trim());
      void qc.invalidateQueries({ queryKey: ['my-orgs'] });
      void qc.invalidateQueries({ queryKey: ['org-general', slug] });
    },
    onError: (err) => {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    },
  });

  if (orgsQuery.isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
        <FormSkeleton rows={5} />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
        <p className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-foreground">
          {t('settings.organization.notFound')}
        </p>
      </div>
    );
  }

  if (org.role !== 'owner') {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
        <p className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-foreground">
          {t('settings.organization.notOwner')}
        </p>
      </div>
    );
  }

  // Org registration number is country-specific (organisationsnummer / Y-tunnus
  // / CVR / …) — hide the field for countries that have no equivalent, and label
  // it with the local term. Mirrors the onboarding ContactFields gate.
  const regTerm = orgNumberTerm(org.country_code);

  const trimmedName = name.trim();
  const nameChanged = trimmedName !== org.name && trimmedName.length > 0;
  const logoChanged = logoKey !== initialLogoKey;
  const streetChanged = street.trim() !== initialStreet;
  const postalChanged = postal.trim() !== initialPostal;
  const officeCityChanged = officeCity.trim() !== initialOfficeCity;
  const coordsChanged = lat !== initialLat || lng !== initialLng;
  const emailChanged = contactEmail.trim() !== initialContactEmail;
  const phoneChanged = contactPhone.trim() !== initialContactPhone;
  const regNumberChanged = regNumber.trim() !== initialRegNumber;
  const dirty =
    nameChanged ||
    logoChanged ||
    streetChanged ||
    postalChanged ||
    officeCityChanged ||
    coordsChanged ||
    emailChanged ||
    phoneChanged ||
    regNumberChanged;
  const nameValid = trimmedName.length > 0 && trimmedName.length <= 200;
  // Email sanity check at the boundary so the server doesn't bounce a typo.
  // Empty is allowed (clears the field).
  const emailValid = contactEmail.trim() === '' || contactEmail.trim().includes('@');
  // Helper to clear the inline error when the user starts editing again
  // — the save toast auto-dismisses on its own.
  const clearStatus = () => {
    setErrorCode(null);
  };

  // Seed text for the address picker: the saved "street, postal city".
  const savedAddressText = [street, [postal, officeCity].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!dirty || !nameValid || !emailValid || save.isPending) return;
    setErrorCode(null);
    save.mutate();
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('settings.title')}
        </p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
          {t('settings.organization.title')}
        </h1>
        <p className="text-muted-foreground">
          {t('settings.organization.subtitle')}
        </p>
      </header>

      {/* Identity card — name + logo */}
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
          >
            <Buildings className="h-5 w-5" weight="bold" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="font-heading text-lg font-bold">
              {t('settings.organization.identity.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.organization.identity.body')}
            </p>
          </div>
        </div>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground">
            {t('settings.organization.nameLabel')}
          </span>
          <Input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrorCode(null);
            }}
            maxLength={200}
            autoComplete="off"
            aria-invalid={!nameValid && name.length > 0 ? true : undefined}
          />
          <span className="text-xs text-muted-foreground">
            {t('settings.organization.nameHint')}
          </span>
        </label>

        <LogoUploadField
          slug={slug}
          token={token}
          value={logoKey}
          label={t('settings.organization.logoLabel')}
          hint={t('settings.organization.logoHint')}
          onChange={(key) => {
            setLogoKey(key);
            setErrorCode(null);
          }}
        />

        {/* Address section — feeds the public website's contact page. */}
        <div className="flex flex-col gap-4 border-t border-border pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
            >
              <MapPin className="h-5 w-5" weight="bold" />
            </span>
            <div className="flex flex-col gap-1">
              <h2 className="font-heading text-lg font-bold">
                {t('settings.organization.address.title')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('settings.organization.address.body')}
              </p>
            </div>
          </div>

          {addressUnavailable ? (
            <>
              {/* Geocoder down (503) — fall back to hand-typed fields. These
                  carry no coordinates, so the club won't get a map pin. */}
              <p className="text-sm font-medium text-foreground/80">
                {t('settings.organization.address.lookupUnavailable')}
              </p>
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground">
                  {t('settings.organization.address.streetLabel')}
                </span>
                <Input
                  type="text"
                  value={street}
                  onChange={(e) => {
                    setStreet(e.target.value);
                    clearStatus();
                  }}
                  maxLength={200}
                  autoComplete="street-address"
                />
              </label>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_2fr]">
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {t('settings.organization.address.postalLabel')}
                  </span>
                  <Input
                    type="text"
                    value={postal}
                    onChange={(e) => {
                      setPostal(e.target.value);
                      clearStatus();
                    }}
                    maxLength={20}
                    autoComplete="postal-code"
                    inputMode="numeric"
                  />
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {t('settings.organization.address.cityLabel')}
                  </span>
                  <Input
                    type="text"
                    value={officeCity}
                    onChange={(e) => {
                      setOfficeCity(e.target.value);
                      clearStatus();
                    }}
                    maxLength={100}
                    autoComplete="address-level2"
                  />
                </label>
              </div>
            </>
          ) : generalQuery.data ? (
            // Address is chosen via Google Places only — never free-typed — so
            // every saved address resolves to real coordinates and a map pin.
            // Keyed on the loaded data so the input seeds with the saved value.
            <AddressAutocomplete
              key={`addr-${generalQuery.dataUpdatedAt}`}
              initialText={savedAddressText}
              regionBias={org.country_code}
              language={(i18n.language || 'en').slice(0, 2)}
              hint={t('settings.organization.address.pickerHint')}
              onPicked={(place) => {
                setStreet(place.street);
                setPostal(place.postal_code);
                setOfficeCity(place.city);
                setLat(place.latitude);
                setLng(place.longitude);
                clearStatus();
              }}
              onLocated={(place) => {
                setLat(place.latitude);
                setLng(place.longitude);
                if (place.street) setStreet(place.street);
                if (place.postal_code) setPostal(place.postal_code);
                if (place.city) setOfficeCity(place.city);
                clearStatus();
              }}
              onUnavailable={() => setAddressUnavailable(true)}
              onClear={() => {
                setStreet('');
                setPostal('');
                setOfficeCity('');
                setLat(null);
                setLng(null);
                clearStatus();
              }}
            />
          ) : null}
        </div>

        {/* Contact section — email, phone, registration number. */}
        <div className="flex flex-col gap-4 border-t border-border pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
            >
              <EnvelopeSimple className="h-5 w-5" weight="bold" />
            </span>
            <div className="flex flex-col gap-1">
              <h2 className="font-heading text-lg font-bold">
                {t('settings.organization.contact.title')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('settings.organization.contact.body')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground">
                {t('settings.organization.contact.emailLabel')}
              </span>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => {
                  setContactEmail(e.target.value);
                  clearStatus();
                }}
                maxLength={200}
                autoComplete="email"
                aria-invalid={!emailValid ? true : undefined}
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground">
                {t('settings.organization.contact.phoneLabel')}
              </span>
              <Input
                type="tel"
                value={contactPhone}
                onChange={(e) => {
                  setContactPhone(e.target.value);
                  clearStatus();
                }}
                maxLength={40}
                autoComplete="tel"
              />
            </label>
          </div>

          {regTerm && (
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground">{regTerm}</span>
              <Input
                type="text"
                value={regNumber}
                onChange={(e) => {
                  setRegNumber(e.target.value);
                  clearStatus();
                }}
                maxLength={40}
                autoComplete="off"
              />
              <span className="text-xs text-muted-foreground">
                {t('settings.organization.contact.regNumberHint')}
              </span>
            </label>
          )}
        </div>

        {errorCode && (
          <p role="alert" className="text-sm text-destructive">
            {t(`settings.organization.error.${errorCode}`, {
              defaultValue: t('settings.organization.error.unknown'),
            })}
          </p>
        )}
        <footer className="flex justify-end">
          <Button
            type="submit"
            disabled={!dirty || !nameValid || !emailValid || save.isPending}
          >
            {save.isPending
              ? t('settings.organization.saving')
              : t('settings.organization.save')}
          </Button>
        </footer>
      </form>

      {/* Member data card — CSV/XLSX import */}
      <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
          >
            <Users className="h-5 w-5" weight="bold" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="font-heading text-lg font-bold">
              {t('settings.organization.importSection.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.organization.importSection.body')}
            </p>
          </div>
        </div>
        <div>
          <Button type="button" variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" />
            {t('settings.organization.importSection.cta')}
          </Button>
        </div>
      </div>

      {/* Location data card — Excel import, AI column mapping + Places resolution */}
      <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
          >
            <MapPin className="h-5 w-5" weight="bold" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="font-heading text-lg font-bold">
              {t('settings.organization.locationImportSection.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.organization.locationImportSection.body')}
            </p>
          </div>
        </div>
        <div>
          <Button type="button" variant="outline" onClick={() => setLocationImportOpen(true)}>
            <Upload className="h-4 w-4" />
            {t('settings.organization.locationImportSection.cta')}
          </Button>
        </div>
      </div>

      <ImportMembersModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        slug={slug}
      />
      {locationImportOpen && (
        <LocationImportDialog slug={slug} onClose={() => setLocationImportOpen(false)} />
      )}
    </div>
  );
}
